import React from 'react';
import { Carousel } from 'primereact/carousel';
import BaseChart from '../form/BaseChart.jsx';

const KPIsIncomeStatementCarousel = ({
    years,
    incomeStatement,
    lineLogarithmicOptions,
    sidewaysBarLogarithmicOptions,
    defaultLogarithmicOptions
}) => {
    const revenue = incomeStatement.map((entry) => Number(entry.totalRevenue)).reverse();
    const netIncome = incomeStatement.map((entry) => Number(entry.netIncome)).reverse();
    const grossProfit = incomeStatement.map((entry) => Number(entry.grossProfit)).reverse();
    const operatingIncome = incomeStatement.map((entry) => Number(entry.operatingIncome)).reverse();
    const ebitda = incomeStatement.map((entry) => Number(entry.ebitda)).reverse();
    const incomeBeforeTax = incomeStatement.map((entry) => Number(entry.incomeBeforeTax)).reverse();
    const interestExpense = incomeStatement.map((entry) => Number(entry.interestExpense)).reverse();
    const operatingExpenses = incomeStatement.map((entry) => Number(entry.operatingExpenses)).reverse();
    const researchAndDevelopment = incomeStatement.map((entry) => Number(entry.researchAndDevelopment)).reverse();
    const sellingGeneralAndAdministrative = incomeStatement
        .map((entry) => Number(entry.sellingGeneralAndAdministrative))
        .reverse();

    const grossMargin = grossProfit.map((gp, i) => (gp / revenue[i]) * 100);
    const operatingMargin = operatingIncome.map((oi, i) => (oi / revenue[i]) * 100);
    const netProfitMargin = netIncome.map((ni, i) => (ni / revenue[i]) * 100);
    const effectiveTaxRate = incomeBeforeTax.map((ibt, i) =>
        incomeBeforeTax[i] !== 0 ? (incomeBeforeTax[i] / ibt) * 100 : 0
    );

    const chartItems = [
        {
            id: 'revenue-vs-net-income',
            title: 'Revenue vs Net Income',
            type: 'line',
            options: lineLogarithmicOptions(),
            data: {
                labels: years,
                datasets: [
                    { label: 'Total Revenue', tension: 0.4, borderColor: '#4E86DE', fill: false, data: revenue },
                    { label: 'Net Income', tension: 0.4, borderColor: '#FFA239', fill: false, data: netIncome }
                ]
            }
        },
        {
            id: 'profitability-ratios',
            title: 'Profitability Ratios',
            type: 'line',
            options: lineLogarithmicOptions(),
            data: {
                labels: years,
                datasets: [
                    { label: 'Gross Margin (%)', tension: 0.4, borderColor: '#879D00', fill: false, data: grossMargin },
                    {
                        label: 'Operating Margin (%)',
                        borderColor: '#FFA239',
                        tension: 0.4,
                        fill: false,
                        data: operatingMargin
                    },
                    {
                        label: 'Net Profit Margin (%)',
                        borderColor: '#AF52DE',
                        tension: 0.4,
                        fill: false,
                        data: netProfitMargin
                    }
                ]
            }
        },
        {
            id: 'debt-and-interest-analysis',
            title: 'Debt & Interest Analysis',
            type: 'line',
            options: lineLogarithmicOptions(),
            data: {
                labels: years,
                datasets: [
                    { label: 'EBITDA', borderColor: '#D03C50', tension: 0.4, fill: false, data: ebitda },
                    {
                        label: 'Income Before Tax',
                        borderColor: '#00D2B3',
                        tension: 0.4,
                        fill: false,
                        data: incomeBeforeTax
                    },
                    {
                        label: 'Interest Expense',
                        borderColor: '#FFA239',
                        tension: 0.4,
                        fill: false,
                        data: interestExpense
                    }
                ]
            }
        },
        {
            id: 'expenses-by-year',
            title: 'Expenses Breakdown',
            type: 'bar',
            options: sidewaysBarLogarithmicOptions('Expenses Breakdown by Year'),
            data: {
                labels: years,
                datasets: [
                    { label: 'Operating Expenses', backgroundColor: '#FFA239', data: operatingExpenses },
                    { label: 'R&D Expenses', backgroundColor: '#4E86DE', data: researchAndDevelopment },
                    { label: 'SG&A Expenses', backgroundColor: '#879D00', data: sellingGeneralAndAdministrative }
                ]
            }
        },
        {
            id: 'tax-analysis',
            title: 'Tax Analysis',
            type: 'bar',
            options: defaultLogarithmicOptions('Tax Analysis by Year'),
            data: {
                labels: years,
                datasets: [
                    { label: 'Income Before Tax', backgroundColor: '#857FF2', data: incomeBeforeTax },
                    { label: 'Income Tax Expense', backgroundColor: '#D03C50', data: effectiveTaxRate }
                ]
            }
        }
    ];

    const chartTemplate = (chart) => {
        return (
            <div className='w-full flex md:justify-center'>
                <div className='w-full md:w-3/4 lg:w-full xl:w-full'>
                    <BaseChart
                        type={chart.type}
                        id={chart.id}
                        title={chart.title}
                        options={chart.options}
                        childClassName='bg-theme1-white p-4 rounded-2xl'
                        className={'mb-6 shadow-lg rounded-2xl mx-3'}
                        data={chart.data}
                    />
                </div>
            </div>
        );
    };

    return (
        <Carousel
            className={'mx-[-10px]'}
            value={chartItems}
            numScroll={1}
            numVisible={1}
            itemTemplate={chartTemplate}
            showNavigators={false}
            circular={false}
            showIndicators
        />
    );
};

export default KPIsIncomeStatementCarousel;
